// Books Reconciliation (V-18) on the Documents page: each period LAMP and a Tally day book were compared for, with the
// totals by status of its latest version, the sheet to download and the lines bill by bill, each of LAMP's opening to
// its bill. A voucher in Tally and not in LAMP is a line for the accounts Role to look at, never a bill. Shown only to
// those the Application API lets see the accounts; nothing here books or changes anything.
import { useEffect, useState } from "react";
import { rupees } from "../api.js";
import { useLanguage, useWords } from "../i18n/words.js";
import { dayOf } from "../lists/List.js";
import {
  RECONCILED_STATUSES,
  type BooksReconciliationApi,
  type BooksSide,
  type ReconciledDocument,
  type ReconciledLine,
  type ReconciledPeriod,
  type Reconciliation,
  type ReconciliationSummary,
} from "./books-reconciliation-api.js";

type Compared = { readonly period: ReconciledPeriod; readonly versions: readonly ReconciliationSummary[] };

const money = (paise: number) => (paise < 0 ? `-${rupees(-paise)}` : rupees(paise));

/** A side's ledgers, each with its cost centres. */
function Ledgers({ side }: { readonly side: BooksSide | undefined }) {
  if (!side) return <>—</>;
  return (
    <ul className="plain">
      {side.ledgers.map((l) => (
        <li key={l.ledger}>
          {l.ledger} {money(l.amount)}
          {l.costCentres
            ? ` (${Object.entries(l.costCentres)
                .map(([centre, amount]) => `${centre} ${money(amount)}`)
                .join(", ")})`
            : null}
        </li>
      ))}
    </ul>
  );
}

function Lines({ api, report }: { readonly api: BooksReconciliationApi; readonly report: Reconciliation }) {
  const w = useWords();
  const { language } = useLanguage();
  const [opened, setOpened] = useState<ReconciledDocument>();
  const title = (line: ReconciledLine) =>
    line.document
      ? (line.document.number ?? line.document.id)
      : `${line.tally?.type ?? ""} ${line.tally?.number ?? ""}`.trim();
  return (
    <>
      <table className="tbl">
        <thead>
          <tr>
            <th scope="col">{w.booksReconciliationBill}</th>
            <th scope="col">{w.documentsDate}</th>
            <th scope="col" />
            <th scope="col">{w.booksReconciliationLamp}</th>
            <th scope="col">{w.booksReconciliationTally}</th>
          </tr>
        </thead>
        <tbody>
          {report.lines.map((line, i) => (
            <tr key={i}>
              <th scope="row">
                {line.href ? (
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => void api.document(line.href ?? "").then(setOpened, () => setOpened(undefined))}
                  >
                    {title(line)}
                  </button>
                ) : (
                  title(line)
                )}
              </th>
              <td>{dayOf(line.lamp?.date ?? line.tally?.date ?? null, language)}</td>
              <td>
                {w.booksReconciliationStatus[line.status]}
                {line.differences.length > 0
                  ? `: ${line.differences.map((d) => w.booksReconciliationDifference[d]).join(", ")}`
                  : null}
                {line.status === "only_in_tally" ? <div className="muted">{w.booksReconciliationLookAt}</div> : null}
              </td>
              <td>
                {line.lamp ? money(line.lamp.amount) : "—"}
                {line.status === "differs" ? <Ledgers side={line.lamp} /> : null}
              </td>
              <td>
                {line.tally ? money(line.tally.amount) : "—"}
                {line.status === "differs" ? <Ledgers side={line.tally} /> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {opened ? (
        <dl role="region" aria-label={opened.object}>
          {Object.entries(opened.fields)
            .filter(([, value]) => typeof value === "string" || typeof value === "number")
            .map(([name, value]) => (
              <div key={name}>
                <dt>{name}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
        </dl>
      ) : null}
    </>
  );
}

function Period({ api, compared }: { readonly api: BooksReconciliationApi; readonly compared: Compared }) {
  const w = useWords();
  const { language } = useLanguage();
  const [versions, setVersions] = useState(compared.versions);
  const [report, setReport] = useState<Reconciliation>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const latest = versions.at(-1);
  if (!latest) return null;
  const act = async (doing: () => Promise<void>) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await doing();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };
  const all = RECONCILED_STATUSES.reduce((sum, s) => sum + latest.totals[s].count, 0);
  return (
    <article>
      <h4>{w.booksReconciliationPeriod(dayOf(compared.period.from, language), dayOf(compared.period.to, language))}</h4>
      <p className="muted">
        {w.booksReconciliationVersion(
          latest.version,
          dayOf(latest.preparedAt.slice(0, 10), language),
          latest.dayBook.name,
        )}
      </p>
      {all > 0 && latest.totals.matched.count === all ? (
        <p>{w.booksReconciliationAllMatched(all)}</p>
      ) : (
        <ul>
          {RECONCILED_STATUSES.filter((s) => latest.totals[s].count > 0).map((s) => (
            <li key={s}>
              {w.booksReconciliationStatus[s]}: {latest.totals[s].count} ({money(latest.totals[s].amount)})
            </li>
          ))}
        </ul>
      )}
      <div className="toolbar">
        <a className="btn" href={latest.sheet} download={latest.name}>
          {w.booksReconciliationSheet}
        </a>
        <button
          type="button"
          className="btn ghost"
          disabled={busy}
          onClick={() => void act(async () => setReport(await api.version(compared.period, latest.version)))}
        >
          {w.booksReconciliationShow}
        </button>
        <button
          type="button"
          className="btn ghost"
          disabled={busy}
          onClick={() =>
            void act(async () => {
              const made = await api.again(compared.period);
              setVersions([...versions, made]);
              setReport(made);
            })
          }
        >
          {w.booksReconciliationAgain}
        </button>
      </div>
      {problem ? <p role="alert">{problem}</p> : null}
      {report ? <Lines api={api} report={report} /> : null}
    </article>
  );
}

/** The periods compared, the latest first; nothing when there are none or the person may not see the accounts. */
export function BooksReconciliation({ api }: { readonly api: BooksReconciliationApi }) {
  const w = useWords();
  const [periods, setPeriods] = useState<readonly Compared[]>();
  useEffect(() => {
    api.periods().then(setPeriods, () => setPeriods(undefined));
  }, [api]);
  if (!periods || periods.length === 0) return null;
  return (
    <section className="books-reconciliation" aria-label={w.booksReconciliationTitle}>
      <h3>{w.booksReconciliationTitle}</h3>
      <p className="muted">{w.booksReconciliationIntro}</p>
      {periods.map((p) => (
        <Period key={`${p.period.from}_${p.period.to}`} api={api} compared={p} />
      ))}
    </section>
  );
}
